/**
 * E.164 normalization for the public sign-up forms.
 *
 * Deliberately NOT libphonenumber-js: ~150 KB gzip of metadata to parse
 * numbers we already know the country of (the user picked it) or that already
 * carry their own `+`. `npm run size` caps the whole entry chunk at 150 KB
 * gzip, so the library alone would blow the budget for one field.
 *
 * MUST agree with supabase/functions/_shared/phone.ts and the SQL
 * public.normalize_phone_e164(): the browser sends E.164, the edge function
 * re-normalizes what it receives, and a BEFORE trigger canonicalizes the row.
 * Dedup only holds if all three land on the same string for the same number.
 */

export interface PhoneCountry {
  /** ISO 3166-1 alpha-2 — i18n key and React key. */
  iso: string;
  /** Country calling code, digits only, no `+`. */
  dial: string;
  flag: string;
  /** National significant number length, [min, max] inclusive. */
  nsn: [number, number];
  /** Example national number, shown as the placeholder. */
  example: string;
}

/**
 * Miami ICP order: the US first, then where our partners actually come from.
 * The Dominican Republic shares +1 with the US (both NANP) and still gets its
 * own row — "+1" on its own tells a Dominican shop owner nothing.
 */
export const PHONE_COUNTRIES: readonly PhoneCountry[] = [
  { iso: "US", dial: "1", flag: "🇺🇸", nsn: [10, 10], example: "305 555 0123" },
  { iso: "CU", dial: "53", flag: "🇨🇺", nsn: [6, 8], example: "5 123 4567" },
  { iso: "VE", dial: "58", flag: "🇻🇪", nsn: [10, 10], example: "412 123 4567" },
  { iso: "CO", dial: "57", flag: "🇨🇴", nsn: [8, 10], example: "300 123 4567" },
  { iso: "MX", dial: "52", flag: "🇲🇽", nsn: [10, 10], example: "55 1234 5678" },
  { iso: "DO", dial: "1", flag: "🇩🇴", nsn: [10, 10], example: "809 555 0123" },
  { iso: "HN", dial: "504", flag: "🇭🇳", nsn: [8, 8], example: "9123 4567" },
  { iso: "NI", dial: "505", flag: "🇳🇮", nsn: [8, 8], example: "8123 4567" },
  { iso: "PE", dial: "51", flag: "🇵🇪", nsn: [8, 9], example: "912 345 678" },
  { iso: "EC", dial: "593", flag: "🇪🇨", nsn: [8, 9], example: "99 123 4567" },
  { iso: "AR", dial: "54", flag: "🇦🇷", nsn: [10, 11], example: "11 2345 6789" },
  { iso: "ES", dial: "34", flag: "🇪🇸", nsn: [9, 9], example: "612 34 56 78" },
];

export const DEFAULT_PHONE_COUNTRY = "US";

/** What the form holds: the picked country plus whatever was typed. */
export interface PhoneValue {
  country: string;
  national: string;
}

export const EMPTY_PHONE: PhoneValue = { country: DEFAULT_PHONE_COUNTRY, national: "" };

export function findPhoneCountry(iso: string): PhoneCountry {
  return PHONE_COUNTRIES.find((c) => c.iso === iso) ?? PHONE_COUNTRIES[0];
}

function fits(length: number, [min, max]: [number, number]): boolean {
  return length >= min && length <= max;
}

/**
 * Digits as typed → national significant number, or null if it can't be one.
 * Order matters: a plausible national number wins over a prefix strip, so a
 * Cuban 53-12-34-56 isn't mistaken for country code 53 plus 123456.
 */
function toNationalNumber(digits: string, country: PhoneCountry): string | null {
  if (fits(digits.length, country.nsn)) return digits;

  // Pasted with the country code but no `+` — "13055550123" under US.
  if (digits.startsWith(country.dial)) {
    const rest = digits.slice(country.dial.length);
    if (fits(rest.length, country.nsn)) return rest;
  }

  // Trunk prefix: most of Latin America and Spain write the national number
  // with a leading 0 that E.164 drops. NANP numbers never start with one.
  if (country.dial !== "1" && digits.startsWith("0")) {
    const rest = digits.replace(/^0+/, "");
    if (fits(rest.length, country.nsn)) return rest;
  }

  return null;
}

/** E.164 (`+13055550123`) or null when the input can't be normalized. */
export function phoneToE164(value: PhoneValue): string | null {
  const raw = value.national.trim();
  if (!raw) return null;

  const digits = raw.replace(/\D/g, "");
  if (!digits) return null;

  // A leading `+` is the user saying the number is already complete, so the
  // selector doesn't get to prepend its own dial code on top of it.
  if (raw.startsWith("+")) {
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }

  const country = findPhoneCountry(value.country);
  const national = toNationalNumber(digits, country);
  return national ? `+${country.dial}${national}` : null;
}
